import { Component, OnDestroy, computed } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { Point, Tooltip } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem, MaterialTicket, MaterialTicketType, ModalSize } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { MaterialComponent } from './material.component'

@Component({
    templateUrl: './materials.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        GridSelectionCalculationComponent,
        NgbNavOutlet,
    ],
})
export class MaterialsComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch
    tooltip = new Tooltip()
    active = 1
    grid: Grid<any>
    haulOffGrid: Grid<any>
    concreteGrid: Grid<MaterialTicket>
    jobMap!: DataMap
    usersAndVendors!: any[]
    userMap!: DataMap
    aggVendors!: any[]
    aggVendorMap!: DataMap
    concreteVendors!: any[]
    concreteVendorsMap!: DataMap
    pumpTruckVendors!: any[]
    pumpTruckVendorMap!: DataMap
    concreteTesterVendors!: any[]
    concreteTesterVendorMap!: DataMap
    concreteTypeMap: DataMap
    pits!: any[]
    materials!: any[]
    materialMap!: DataMap
    userTruck!: string
    availableTrucks!: any[]
    availableTrailers!: any[]
    itemIds: any[] = []

    /** Rolling month keys (`yyyy-MM`) for toolbar multi-select; independent checked state per tab. */
    monthFilterSandGravel: ListItem<string>[]
    monthFilterHaulOff: ListItem<string>[]
    monthFilterConcrete: ListItem<string>[]

    private static readonly monthFilterRangeMonths = 36

    private buildMonthFilterItems(): ListItem<string>[] {
        const items: ListItem<string>[] = []
        const now = new Date()
        for (let i = 0; i < MaterialsComponent.monthFilterRangeMonths; i++) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
            const y = d.getFullYear()
            const mo = d.getMonth() + 1
            const value = `${y}-${mo.toString().padStart(2, '0')}`
            const display = `${d.toLocaleString('en-US', { month: 'short' })} ${y}`
            items.push(new ListItem(value, display))
        }
        return items
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.OperationsMaterialsEdit])
    }

    isLessor = computed(() => this.services.authentication.isLessor())

    constructor() {
        super()
        this.grid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.materialTickets,
                'Material Ticket',
                MaterialComponent,
                () => ({}),
                { component: this, materialType: MaterialTicketType.SandGravel },
                ModalSize.extraLarge,
            )
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => [item.job, item.ticket, item.invoiceNumber])

        this.haulOffGrid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.materialTickets,
                'Haul Off Ticket',
                MaterialComponent,
                () => ({}),
                { component: this, materialType: MaterialTicketType.HaulOff },
                ModalSize.extraLarge,
            )
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => [item.job, item.ticket, item.invoiceNumber])

        this.concreteGrid = new Grid<any>(this.services)
            .setupModalEditing(
                this.services.data.materialTickets,
                'Concrete Ticket',
                MaterialComponent,
                () => ({}),
                { component: this, materialType: MaterialTicketType.Concrete },
                ModalSize.extraLarge,
            )
            .wrapColumnHeaders()
            .isReadOnly(false)
            .filterBy(item => [item.job, item.ticket, item.invoiceNumber])

        // The image URLs come from the server, so when we add or edit items, we need to refetch to get the URLS.
        this.grid.modified.on(this.refreshItems.bind(this))
        this.haulOffGrid.modified.on(this.refreshItems.bind(this))
        this.concreteGrid.modified.on(this.refreshItems.bind(this))

        this.monthFilterSandGravel = this.buildMonthFilterItems()
        this.monthFilterHaulOff = this.buildMonthFilterItems()
        this.monthFilterConcrete = this.buildMonthFilterItems()

        const concreteTypeMap = [new ListItem('CONCRETE', 'Concrete'), new ListItem('FLOWFILL', 'Flowfill')]
        this.concreteTypeMap = new DataMap(concreteTypeMap, 'value', 'display')

        this.refresh()
    }

    ngOnDestroy() {
        this.grid.modified.off(this.refreshItems)
        this.haulOffGrid.modified.off(this.refreshItems)
        this.concreteGrid.modified.off(this.refreshItems)
    }

    refreshItems() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.materialTickets.queryAll()).subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.grid.items = result[0]
                    this.haulOffGrid.items = result[0]
                    this.concreteGrid.items = result[0]
                },
            }),
        )
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.get('api/VpUSer/GetVistaUsersAndVendors'),
                    this.services.data.get('api/Vendor/GetForAggregate'),
                    this.services.data.get('api/VpMaterial/GetMaterialPits'),
                    this.services.data.get('api/VpMaterial/GetMaterials'),
                    this.services.data.get('api/VpEquipment/GetUserAssignedEquipment'),
                    this.services.data.get('api/VpEquipment/GetMaterialTrucks'),
                    this.services.data.get('api/VpEquipment/GetMaterialTrailers'),
                    this.services.data.get('api/Vendor/GetForConcrete'),
                    this.services.data.get('api/Vendor/GetForPumpTruck'),
                    this.services.data.get('api/Vendor/GetForConcreteTester'),
                )
                .subscribe({
                    next: result => {
                        // Each grid shows a different type of material ticket, so we set the filters here.
                        this.grid.view.filters.push(x => x.materialType === MaterialTicketType.SandGravel)
                        this.haulOffGrid.view.filters.push(x => x.materialType === MaterialTicketType.HaulOff)
                        this.concreteGrid.view.filters.push(x => x.materialType === MaterialTicketType.Concrete)

                        const jobMap = this.services.data.vpJobs.items.map(x => new ListItem(x.id, x.id + ' ' + x.name))
                        this.jobMap = new DataMap(jobMap, 'value', 'display')

                        this.usersAndVendors = result[1]
                        this.userMap = new DataMap(this.usersAndVendors, 'userHrRefOrVendorId', 'name')

                        this.aggVendors = result[2]
                        this.aggVendorMap = new DataMap(this.aggVendors, 'id', 'name')
                        this.concreteVendors = result[8]
                        this.concreteVendorsMap = new DataMap(this.concreteVendors, 'id', 'name')
                        this.pumpTruckVendors = result[9]
                        this.pumpTruckVendorMap = new DataMap(this.pumpTruckVendors, 'id', 'name')
                        this.concreteTesterVendors = result[10]
                        this.concreteTesterVendorMap = new DataMap(this.concreteTesterVendors, 'id', 'name')

                        this.pits = this.services.data.materialPits.items = result[3]
                        this.materials = this.services.data.materials.items = result[4]
                        this.userTruck = result[5].value
                        this.availableTrucks = result[6]
                        this.availableTrailers = result[7]

                        this.refreshItems()
                    },
                }),
        )
    }

    itemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            cell.classList.remove('yellow-cell')
            if (col.binding === 'invoiceNumber' || col.binding === 'invoiceDate') {
                cell.classList.add('yellow-cell')
            }
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) return
        this.bulkUpdate([flexGrid.rows[args.row].dataItem])
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem
            itemIds.push(dataItem.id)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        this.itemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter(x => this.itemIds.indexOf(x.id) > -1)
        this.bulkUpdate(itemsToSave)
    }

    bulkUpdate(items: MaterialTicket[]) {
        this.services.data.put('api/MaterialTicket/BulkUpdate', items).subscribe(
            () => {
                this.services.data.materialTickets.updateSort()
                this.grid.refresh()
            },
            () => {
                this.services.notification.error('The invoicing changes were not successfully saved')
            },
        )
    }

    getExistingImageSrc(url: string) {
        if (url.indexOf('.pdf') > -1) {
            return './Resources/pdfImage.png?w=500'
        }
        return './Resources/Images/Material/' + url + '?w=500'
    }

    openImage(url) {
        window.open('./Resources/Images/Material/' + url, '_blank')
    }

    tabChanged() {
        // Trigger a window resize to get a consistent modal size, since the expanding modal and timing of the grid is uncertain.
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }

    private applyMonthToolbarFilter(grid: Grid<any>, monthItems: ListItem<string>[]) {
        const view = grid.view

        if (view.filters.length > 2) {
            view.filters.pop()
        }

        const checkedKeys = monthItems.filter(m => m.$checked).map(m => m.value)
        if (checkedKeys.length > 0) {
            const keySet = new Set(checkedKeys)
            view.filters.push((x: MaterialTicket) => {
                const d = x.date
                const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                return keySet.has(k)
            })
        }

        grid.refresh()
    }

    applySandGravelMonthFilter() {
        this.applyMonthToolbarFilter(this.grid, this.monthFilterSandGravel)
        setTimeout(() => (this.grid.flexGrid.scrollPosition = new Point(0, 0)), 20)
    }

    applyHaulOffMonthFilter() {
        this.applyMonthToolbarFilter(this.haulOffGrid, this.monthFilterHaulOff)
        setTimeout(() => (this.haulOffGrid.flexGrid.scrollPosition = new Point(0, 0)), 20)
    }

    applyConcreteMonthFilter() {
        this.applyMonthToolbarFilter(this.concreteGrid, this.monthFilterConcrete)
        setTimeout(() => (this.concreteGrid.flexGrid.scrollPosition = new Point(0, 0)), 20)
    }
}
